import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Alert, AlertTitle, Box, Button, Card, CardActions, CircularProgress, Grid, Link, Stack, TextField, Typography,
} from '@mui/material';
import { AppBadge, AppCardSection, AppDescriptionList, AppPage, AppSelect, AppToast, ImplicitSubmit } from '@shared';

import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import { adminClient, userErrorsToFormik } from 'api/index';

import PageSkeleton from './PageSkeleton';
import snakeCase from 'lodash/snakeCase';
import I18n from 'i18n-js';

import AppBooleanBadge from '@components/shared/ui/AppBooleanBadge';
import AppModal from 'components/shared/AppModal';

import CustomerDataSection from './components/CustomerDataSection';
import CustomerTicketComments from './components/CustomerTicketComments';
import CustomerTicketAdminMedia from './components/CustomerTicketAdminMedia';
import CustomerLaneSection from './components/CustomerLaneSection';
import SupplierCaseSection from './components/SupplierCaseSection';

import RelatedRecordsSection from './components/RelatedRecordsSection';

import { buildDescriptionListItems } from 'utils/index';

const propTypes = {
  recordId: PropTypes.number.isRequired,
}

const defaultProps = {

}

const CustomerTicketDetails = (props) => {
  const [toast, setToast] = useState(null);

  // =================== API Hooks ==========================
  const { data, loading, error, refetch } = useQuery(RECORD_GET, {
    fetchPolicy: 'network-only',
    variables: {
      id: props.recordId
    }
  });

  // =======================================================

  // >>> First loading <<<
  // Load skeleton if required data hasn't loaded at least once
  if(!data){
    return <PageSkeleton />
  }

  const customerTicket = data.customerTicket;
  const actionsLoading = loading; // Loader on top of content

  const adminDataItems = buildDescriptionListItems([
    // requireItems - Only display if true or when ticket has been approved/rejected meaning requiral has been explicitly set.
    // Not shown from the start to avoid confusion as "No" required can be interpreted as not required but also as not set.
    { name: "approvalKind", hideIfEmpty: true, accessor: (value) => I18n.t(`enum.approval_kind.${snakeCase(value)}`) },
    {
      name: "requireItems", 
      accessor: (value) => <AppBooleanBadge value={value} />, 
      // Hidden while the approval kind is shown: it already says whether the product is required
      hideIf: (value, record) => !record.requireItemsDisplayable || !!record.approvalKind
    },
    { name: "itemsReceived", accessor: (value) => <AppBooleanBadge value={value} /> },
    { name: "brightpearlSalesCreditId", hideIfEmpty: true, accessor: (value, record) => 
      <Link href={record.brightpearlSalesCreditUrl} target="_blank" rel="noopener">{value}</Link>
    },
    { name: "createdAt", accessor: (value) => I18n.l("time.formats.app_datetime", value) },
  ], customerTicket);

  // Just links to other CustomerTickets related to the same order
  const relatedOrderCustomerTickets = customerTicket.relatedOrderCustomerTickets.map(ticket => {
    return {
      term: ticket.name,
      description: (
        <Link href={`/customer_tickets/${ticket.displayId}`} target="_blank" rel="noopener">
          { ticket.name } - { I18n.t(`enum.ticket_kind.${snakeCase(ticket.ticketKind)}`) }
        </Link>
      )
    }
  });

  return (
    <AppPage
      fullWidth
      title={
        <>
          <span>{ customerTicket.name }</span>
          { customerTicket.keyCustomerName &&
            <span className='ml8'><AppBadge status="info">{ customerTicket.keyCustomerName }</AppBadge></span>
          }
          <span className='ml8'>{ actionsLoading && <CircularProgress size={16} /> }</span>
        </>
      }
      breadcrumbs={[{ content: I18n.t("app.customer_tickets.title"), url: "/customer_tickets" }]}
    >
      { !!toast && <AppToast content={toast} onDismiss={() => setToast(null)} /> }

      <Grid container spacing={2}>
        {/* Left section - what to do next: the two lanes first, then the conversation around them */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            {customerTicket.awaitingPickupLocation &&
              <PickupLocationPendingBanner customerTicketId={customerTicket.id} customerEmail={customerTicket.customerEmail}
                onToast={setToast} onRefetch={refetch}>
                {/* The customer may have brought it anyway: receiving it here ends the wait for the location */}
                {customerTicket.receivableWithoutTask &&
                  <ReceiveWithoutTaskAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} asButton />
                }
              </PickupLocationPendingBanner>
            }

            {/* One predicate, shared with the model, so the page and the backend cannot drift into
                offering an action the backend refuses. */}
            {customerTicket.receivableAtStore && !customerTicket.productBroughtAnyway &&
              <MarkReceivedAtStoreAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} />
            }

            {/* No task left to bring the product (never created, or archived): staff receives it directly.
                A store drop-off not yet handed over goes through the store first, so only one banner shows. */}
            {customerTicket.receivableWithoutTask && !customerTicket.receivableAtStore && !customerTicket.awaitingPickupLocation &&
              !customerTicket.productBroughtAnyway &&
              // A resolution already chosen (closed, or a gift card still processing) leaves none to choose
              <ReceiveWithoutTaskAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch}
                customerResolved={customerTicket.readOnly || (!!customerTicket.resolutionAction && customerTicket.resolutionAction !== 'PENDING')} />
            }

            {/* Nothing is pending here: the product was not required. Offered quietly, for when the customer brings it anyway,
                and only while one of the receptions is open (not with the store → CEDI transfer still pending) */}
            {customerTicket.productBroughtAnyway && (customerTicket.receivableAtStore || customerTicket.receivableWithoutTask) &&
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Producto no requerido. Si el cliente lo entregó igual, regístralo:</Typography>
                { customerTicket.receivableAtStore
                  ? <MarkReceivedAtStoreAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} asButton />
                  : <ReceiveWithoutTaskAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} asButton
                      customerResolved={customerTicket.readOnly || (!!customerTicket.resolutionAction && customerTicket.resolutionAction !== 'PENDING')} />
                }
              </Box>
            }

            {/* The workshop's visit ruled for a change or credit note: the product has to come in after all */}
            {customerTicket.productRequirable &&
              <RequireProductAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} />
            }

            {/* The two lanes of the case, same anatomy, resolved independently */}
            <CustomerLaneSection
              customerTicket={customerTicket}
              onToast={setToast}
              onRefetch={refetch}
              footer={!customerTicket.readOnly && (
                <TicketCancelButton customerTicket={customerTicket} onToast={setToast} onRefetch={refetch} />
              )}
            />

            <SupplierCaseSection
              customerTicket={customerTicket}
              onToast={setToast}
              onRefetch={refetch}
            />

            <CustomerTicketComments
              customerTicketId={customerTicket.id}
              onToast={setToast}
              as={ ({ children }) =>
                <Card>
                  <AppCardSection title="Comentarios">
                    { children }
                  </AppCardSection>
                </Card>
              }
            />

          </Stack>
        </Grid>

        {/* Right section - who the case is about, then admin and related records */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <Card>
              <CustomerDataSection customerTicket={customerTicket} />
            </Card>

            <Card>
              <AppCardSection title="Admin">
                <AppDescriptionList
                  items={adminDataItems}
                />
              </AppCardSection>
            </Card>

            { relatedOrderCustomerTickets.length > 0 &&
              <Card>
                <AppCardSection title="RMA relacionados a la Orden">
                  <AppDescriptionList
                    items={relatedOrderCustomerTickets}
                  />
                </AppCardSection>
              </Card>
            }

            <Card>
              <RelatedRecordsSection customerTicket={customerTicket} />
            </Card>

            <CustomerTicketAdminMedia
              customerTicketId={customerTicket.id}
              onToast={setToast}
              as={ ({ children, primaryFooterAction }) =>
                <Card>
                  <AppCardSection title="Admin media">
                    { children }
                  </AppCardSection>

                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      disabled={ primaryFooterAction.loading }
                      onClick={ primaryFooterAction.onAction }
                    >
                      { primaryFooterAction.content }
                    </Button>
                  </CardActions>
                </Card>
              }
            />
          </Stack>
        </Grid>
      </Grid>
    </AppPage>
  )
};

const PickupLocationPendingBanner = ({ customerTicketId, customerEmail, onToast, onRefetch, children }) => {
  const [regenerateLink, { loading }] = useMutation(REGENERATE_PICKUP_LOCATION_LINK, {
    onCompleted: (data) => {
      const { payload, errors } = data.customerTicketRegeneratePickupLocationLink;
      if (payload) {
        navigator.clipboard.writeText(payload)
          .then(() => onToast("Link copiado al portapapeles"))
          .catch(() => onToast("No pudimos copiar el link al portapapeles"));
      } else {
        onToast(errors[0]?.message || I18n.t("messages.one_error"));
      }
    },
    onError: () => onToast(I18n.t("messages.request_error")),
  });

  const handleCopyLink = () => regenerateLink({ variables: { id: customerTicketId } });

  // Refetches so the resend shows up in the history, which is where the retries get counted
  const [resendEmail, { loading: resending }] = useMutation(RESEND_PICKUP_LOCATION_EMAIL, {
    onCompleted: (data) => {
      const { success, errors } = data.customerTicketResendPickupLocationEmail;
      if (success) {
        onToast(`Correo reenviado a ${customerEmail}`);
        onRefetch();
      } else {
        onToast(errors[0]?.message || I18n.t("messages.one_error"));
      }
    },
    onError: () => onToast(I18n.t("messages.request_error")),
  });

  const handleResendEmail = () => resendEmail({ variables: { id: customerTicketId } });

  return (
    <Alert
      severity="warning"
      action={
        <Stack direction="row" spacing={1}>
          <Button color="inherit" size="small" disabled={resending} onClick={handleResendEmail}>
            Reenviar correo
          </Button>
          <Button color="inherit" size="small" disabled={loading} onClick={handleCopyLink}>
            Generar y copiar nuevo link
          </Button>
        </Stack>
      }
    >
      <AlertTitle>Esperando ubicación del cliente</AlertTitle>
      <p>El cliente todavía no ha confirmado dónde retirar el producto.</p>
      {children && <div className="mt12">{children}</div>}
    </Alert>
  );
};

// Row 1 of the RMA product-tracking automation table - creates the store -> CEDI Task that
// propagate_rma_leg_transition!/register_reception_at_cedi! then advance automatically.
// Store choices come from the real PickupPoint catalog (Unimart's actual retail locations) rather
// than free text - mark_received_at_store! still only takes a plain address string, so we send the
// selected store's name as that string; PickupPoint has no street-address field of its own.
// asButton renders only the button, for a page that offers it as a quiet option instead of a pending warning
const MarkReceivedAtStoreAction = ({ customerTicketId, onToast, onRefetch, asButton = false }) => {
  const modalRef = useRef(null);
  const [pickupPointName, setPickupPointName] = useState('');
  const [formError, setFormError] = useState(null);

  // The selector lists every store, so one capped page would silently hide the ones past it and the
  // operator could not pick them. Page 1 arrives first and the rest are pulled in behind it.
  const { data: pickupPointsData, fetchMore: fetchMorePickupPoints } = useQuery(
    MARK_RECEIVED_PICKUP_POINTS_QUERY, { client: adminClient, variables: { page: 1 } },
  );
  const loadedPage = pickupPointsData?.pickupPoints?.metadata?.page ?? 0;
  const totalPages = pickupPointsData?.pickupPoints?.metadata?.pages ?? 0;

  useEffect(() => {
    if (loadedPage === 0 || loadedPage >= totalPages) return;

    fetchMorePickupPoints({
      variables: { page: loadedPage + 1 },
      updateQuery: (previous, { fetchMoreResult }) => (
        fetchMoreResult
          ? {
            pickupPoints: {
              ...fetchMoreResult.pickupPoints,
              items: [...previous.pickupPoints.items, ...fetchMoreResult.pickupPoints.items],
            },
          }
          : previous
      ),
    });
  }, [loadedPage, totalPages]);

  const storeOptions = [
    { label: 'Selecciona una tienda...', value: '' },
    ...(pickupPointsData?.pickupPoints?.items ?? [])
      .filter((point) => point.active)
      .map((point) => ({ label: point.name, value: point.name })),
  ];

  const [markReceivedAtStore, { loading }] = useMutation(MARK_RECEIVED_AT_STORE, {
    onCompleted: (data) => {
      const { customerTicket, errors } = data.customerTicketMarkReceivedAtStore;
      if (customerTicket) {
        onToast('Producto marcado como recibido en tienda');
        modalRef.current?.close();
        onRefetch();
      } else {
        setFormError(errors?.[0]?.message || I18n.t('messages.one_error'));
      }
    },
    onError: () => setFormError(I18n.t('messages.request_error')),
  });

  const openModal = () => {
    setPickupPointName('');
    setFormError(null);
    modalRef.current?.open();
  };

  const handleSubmit = () => {
    setFormError(null);
    markReceivedAtStore({ variables: { id: customerTicketId, address: pickupPointName } });
  };

  return (
    <>
      { asButton ?
        <Button variant="outlined" color="inherit" onClick={openModal}>Marcar recibido en tienda</Button>
      :
        <Alert severity="warning">
          <AlertTitle>Producto pendiente de recibir en tienda</AlertTitle>
          <p>Marca el producto como recibido para iniciar su traslado a CEDI.</p>
          <div className="mt12">
            <Button variant="outlined" color="inherit" onClick={openModal}>Marcar recibido en tienda</Button>
          </div>
        </Alert>
      }

      <AppModal
        ref={modalRef}
        title="Marcar recibido en tienda"
        primaryAction={{
          content: 'Confirmar',
          onAction: handleSubmit,
          loading,
          disabled: !pickupPointName,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => modalRef.current?.close() }]}
      >
        {formError && (
          <div className="mb8">
            <Alert severity="error">{formError}</Alert>
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <AppSelect
            label="Tienda"
            options={storeOptions}
            value={pickupPointName}
            onChange={setPickupPointName}
          />
          <ImplicitSubmit />
        </form>
      </AppModal>
    </>
  );
};

// Same outcome as accepting the store -> CEDI transfer, for a ticket with no pending lane task.
// asButton renders only the button, for a banner that already explains the situation
const ReceiveWithoutTaskAction = ({ customerTicketId, onToast, onRefetch, asButton = false, customerResolved = false }) => {
  const modalRef = useRef(null);
  const [formError, setFormError] = useState(null);

  const [receiveWithoutTask, { loading }] = useMutation(RECEIVE_WITHOUT_TASK, {
    onCompleted: (data) => {
      const { customerTicket, errors } = data.customerTicketReceiveWithoutTask;
      if (customerTicket) {
        onToast('Producto recibido en CEDI');
        modalRef.current?.close();
        onRefetch();
      } else {
        setFormError(errors?.[0]?.message || I18n.t('messages.one_error'));
      }
    },
    onError: () => setFormError(I18n.t('messages.request_error')),
  });

  const openModal = () => {
    setFormError(null);
    modalRef.current?.open();
  };

  return (
    <>
      { asButton ?
        <Button variant="outlined" color="inherit" onClick={openModal}>Recibir producto</Button>
      :
        <Alert severity="warning">
          <AlertTitle>Producto pendiente de recibir, sin tarea asociada</AlertTitle>
          <p>Este RMA no tiene una tarea pendiente que traiga el producto. Si ya llegó al CEDI, regístralo aquí.</p>
          <div className="mt12">
            <Button variant="outlined" color="inherit" onClick={openModal}>Recibir producto</Button>
          </div>
        </Alert>
      }

      <AppModal
        ref={modalRef}
        title="Recibir producto en CEDI"
        primaryAction={{
          content: 'Confirmar recepción',
          onAction: () => receiveWithoutTask({ variables: { id: customerTicketId } }),
          loading,
          disabled: false,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => modalRef.current?.close() }]}
      >
        {formError && (
          <div className="mb8">
            <Alert severity="error">{formError}</Alert>
          </div>
        )}
        {/* A customer already resolved has no refund left to choose: the product is now for the supplier lane */}
        <p>
          { customerResolved
            ? 'El producto queda en CEDI y se habilita Enviar a proveedor/taller. La resolución del cliente no cambia.'
            : 'El producto queda en CEDI y se habilita Elegir reintegro, igual que al aceptar el traslado de una tarea.' }
        </p>
      </AppModal>
    </>
  );
};

// Turns the technical visit into a ticket that waits for the product, so its pickup or store drop-off opens up
const RequireProductAction = ({ customerTicketId, onToast, onRefetch }) => {
  const modalRef = useRef(null);
  const [formError, setFormError] = useState(null);

  const [requireProduct, { loading }] = useMutation(REQUIRE_PRODUCT, {
    onCompleted: (data) => {
      const { customerTicket, errors } = data.customerTicketRequireProduct;
      if (customerTicket) {
        onToast('El RMA ahora espera el producto');
        modalRef.current?.close();
        onRefetch();
      } else {
        setFormError(errors?.[0]?.message || I18n.t('messages.one_error'));
      }
    },
    onError: () => setFormError(I18n.t('messages.request_error')),
  });

  const openModal = () => {
    setFormError(null);
    modalRef.current?.open();
  };

  return (
    <>
      <Alert severity="info">
        <AlertTitle>Esperando visita técnica</AlertTitle>
        <p>Si el taller indicó cambio de producto o nota de crédito, el producto tiene que venir al CEDI.</p>
        <div className="mt12">
          <Button variant="outlined" color="inherit" onClick={openModal}>El producto viene</Button>
        </div>
      </Alert>

      <AppModal
        ref={modalRef}
        title="El producto viene al CEDI"
        primaryAction={{
          content: 'Confirmar',
          onAction: () => requireProduct({ variables: { id: customerTicketId } }),
          loading,
          disabled: false,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => modalRef.current?.close() }]}
      >
        {formError && (
          <div className="mb8">
            <Alert severity="error">{formError}</Alert>
          </div>
        )}
        <p>
          El RMA pasa a esperar el producto, como si se hubiera aprobado con producto requerido: se habilitan la recolección
          o la recepción en tienda, y el cliente recibe el aviso correspondiente. La nota de crédito se crea al recibir el producto.
        </p>
      </AppModal>
    </>
  );
};

const REQUIRE_PRODUCT = gql`
mutation($id: ID!){
  customerTicketRequireProduct(id: $id) {
    customerTicket {
      id
      status
    }
    errors {
      key
      message
    }
  }
}
`;

const RECEIVE_WITHOUT_TASK = gql`
mutation($id: ID!){
  customerTicketReceiveWithoutTask(id: $id) {
    customerTicket {
      id
      productStatus
    }
    errors {
      key
      message
    }
  }
}
`;

const MARK_RECEIVED_PICKUP_POINTS_QUERY = gql`
  query MarkReceivedAtStorePickupPoints($page: Int!) {
    pickupPoints(pagination: { page: $page, items: 100 }) {
      items {
        id
        name
        active
      }
      metadata {
        page
        pages
      }
    }
  }
`;

const TicketCancelButton = ({ customerTicket, onToast, onRefetch }) => {
  const [isLoading, setIsLoading] = useState(false);
  const cancellationRef = useRef(null);
  
  return (
    <>
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="error"
          disabled={isLoading}
          startIcon={ isLoading ? <CircularProgress size={16} color="inherit" /> : null }
          onClick={() => cancellationRef.current?.handleCancelAction()}
        >
          Cancelar solicitud
        </Button>
      </Box>
      <CustomerTicketCancellation 
        ref={cancellationRef}
        customerTicket={customerTicket}
        onToast={onToast}
        onRefetch={onRefetch}
        onLoadingChange={setIsLoading}
      />
    </>
  );
};

const CustomerTicketCancellation = React.forwardRef(({ customerTicket, onToast, onRefetch, onLoadingChange }, ref) => {
  const modalRef = useRef(null);
  
  const [formState, setFormState] = useState({
    reason: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [cancelTicket, { loading: loadingCancel }] = useMutation(CANCEL_TICKET, {
    onCompleted: (dataCancel) => {
      const customerTicketUpdated = dataCancel.customerTicketCancel.customerTicket;
      if(customerTicketUpdated?.id){
        onToast(I18n.t("messages.update_successful"));
        modalRef.current?.close();
        onRefetch();
      }
      else{
        console.log("Errors", dataCancel);
        onToast(I18n.t("messages.one_error"));
      }
      setIsSubmitting(false);
      onLoadingChange(false);
    },
    onError: (error) => {
      console.log("Error", error);
      onToast(I18n.t("messages.request_error"));
      setIsSubmitting(false);
      onLoadingChange(false);
    }
  });

  useEffect(() => {
    onLoadingChange(isSubmitting || loadingCancel);
  }, [isSubmitting, loadingCancel, onLoadingChange]);

  const handleCancelAction = () => {
    modalRef.current?.open();
  };

  React.useImperativeHandle(ref, () => ({
    handleCancelAction
  }));

  const handleSubmit = () => {
    setIsSubmitting(true);
    onLoadingChange(true);
    cancelTicket({
      variables: {
        id: customerTicket.id,
        reason: formState.reason
      }
    });
  };

  return (
    <>
      <AppModal
        ref={modalRef}
        title="Cancelar solicitud"
        primaryAction={{
          content: "Cancelar",
          onAction: handleSubmit,
          destructive: true,
          loading: isSubmitting || loadingCancel,
        }}
        secondaryActions={[
          { content: "Volver", onAction: () => modalRef.current?.close() },
        ]}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <TextField
            fullWidth
            className='mb8'
            label="Razón de cancelación"
            value={formState.reason}
            onChange={(event) => setFormState({...formState, reason: event.target.value})}
            multiline
            minRows={4}
            placeholder="Indique el motivo de la cancelación..."
            InputLabelProps={{ shrink: true }}
          />
          <ImplicitSubmit />
        </form>
      </AppModal>
    </>
  );
});

const CustomerTicketDetailsWithApollo = (props) => {
  return (
    <ApolloProvider client={adminClient}>
      <CustomerTicketDetails {...props} />
    </ApolloProvider>
  )
};

const RECORD_GET = gql`
  fragment TeamData on Team {
    id
    name
  }

  fragment CourierData on Courier{
    id
    name
    team{
      ...TeamData
    }
  }

  query($id: ID!){
    customerTicket(id: $id) {
      id
      name
      customerKind
      providerName
      providerCustomerId
      customerEmail
      customerName
      keyCustomerName
      customerPhone
      providerLineItemId
      moneyProductOriginalTotal
      productTitle
      productSku
      productImageUrl
      ticketKind
      reason
      description
      resolutionPreference
      retrievalMethod
      receivableAtStore
      receivableWithoutTask
      productBroughtAnyway
      productRequirable
      retrievalNotes
      retrievalDate
      address
      lat
      lon
      province
      canton
      district
      zip
      awaitingPickupLocation
      brightpearlSalesCreditId

      status
      requireItems
      approvalKind
      itemsReceived
      productStatus
      resolutionAction
      laneDisplayId
      pendingAction
      slaConsumedPercent
      slaState
      promiseDueAt
      promiseBreached
      createdAt
      rejectReason

      sendableToSupplier
      conditionedOnWorkshop
      assignedTo {
        id
        name
      }
      supplierCase {
        id
        displayId
        pendingAction
        slaConsumedPercent
        slaState
        status
        supplierResolution
        blocksCustomerResolution
        supplierCreditNoteReference
        workshopCaseNumber
        creditNoteIssuedOn
        workshopCaseNumberEditable
        readOnly
        notes
        sentAt
        slaDueAt
        workshop {
          id
          name
          contact
        }
        destination {
          id
          name
          address
          contact
        }
        supplier {
          id
          name
        }
        assignedTo {
          id
          name
        }
        resolvable
        creditNotePending
        returnTaskCreatable
        workshopDeliveryRegistrable
        returnReceivableWithoutTask
        tasks {
                  id
          name
          status
          rmaLeg
          movement
          movementLabel
          routeOrigin
          routeDestination
          adminUrl
          archived
          team { id name }
          courier {
            id
            name
            team { id name }
          }
        }
        resourceLogs {
          id
          displayMessage
          eventDatetime
          notes
        }
      } # supplierCase

      relatedOrderCustomerTickets {
        id
        name
        ticketKind
        displayId
      }

      media{
        url
        identifier
        contentType
      }

      resourceLogs{
        id
        displayMessage
        eventDatetime
        notes
      }

      relatedDeliveryRecordType
      relatedDeliveryRecordId

      pickupPoint{
        id
        name
        address
        schedule
      }

      displayProviderOrderId
      providerOrderUrl
      brightpearlSalesCreditUrl
      approvable
      rejectable
      escalable
      resolutionActionable
      readOnly
      requireItemsDisplayable

      task{
        id
        name
        status
        createdAt
        adminUrl
        courier{
          ...CourierData
        }
      } # task

      customerLaneTasks {
                id
          name
          status
          rmaLeg
          movement
          movementLabel
          routeOrigin
          routeDestination
          adminUrl
          archived
          team { id name }
          courier {
            id
            name
            team { id name }
          }
      }

      relatedDeliveryRecord{
        ... on Order{
          id
          orderNumber
          adminUrl

          courier{
            ...CourierData
          }
        } # on Order

        ... on DispatchOrder{
          id
          name
          orderNumber

          dispatchControl{
            id
            name
            team{
              ...TeamData
            }
          }
        } # on DispatchOrder
      } # relatedDeliveryRecord
      
    } # customerTicket
  }
`;

const CANCEL_TICKET = gql`
mutation($id: ID!, $reason: String!){
  customerTicketCancel(id: $id, reason: $reason) {
    customerTicket {
      id
    }
    errors {
      key
      message
    }
  }
}
`;

const REGENERATE_PICKUP_LOCATION_LINK = gql`
mutation($id: ID!){
  customerTicketRegeneratePickupLocationLink(id: $id) {
    payload
    errors {
      key
      message
    }
  }
}
`;

const RESEND_PICKUP_LOCATION_EMAIL = gql`
mutation($id: ID!){
  customerTicketResendPickupLocationEmail(id: $id) {
    success
    errors {
      key
      message
    }
  }
}
`;

const MARK_RECEIVED_AT_STORE = gql`
mutation($id: ID!, $address: String!){
  customerTicketMarkReceivedAtStore(id: $id, address: $address) {
    customerTicket {
      id
      productStatus
    }
    errors {
      key
      message
    }
  }
}
`;

CustomerTicketDetails.propTypes = propTypes;
CustomerTicketDetails.defaultProps = defaultProps;

export default CustomerTicketDetailsWithApollo;